import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import { ThemeProvider } from '@/components/theme-provider'
import { SessionProvider } from './contexts/SessionContext'
import router from '@/router/router'
import '@/index.css'
import { Provider } from 'react-redux'
import store from './services/store'
import * as Sentry from '@sentry/react'

// PWA IMPORTS
import { indexedDBService } from './utils/indexedDB'
import { cacheService } from './utils/cacheService'
import { locationService } from './utils/locationService'

// 🛡️ PRODUCTION-ONLY SENTRY SETUP
const initSentry = () => {
  // Only initialize in production environment
  if (process.env.NODE_ENV !== 'production') {
    return
  }

  // Only track on production domain
  if (window.location.hostname.includes('localhost') || 
      window.location.hostname.includes('127.0.0.1') ||
      window.location.hostname.includes('netlify.app') ||
      !window.location.hostname.includes('staffluent.co')) {
    return
  }

  
  Sentry.init({
    dsn: 'https://45748a69284990f4cdf6ecf7651c6b1b@o4508410395230208.ingest.de.sentry.io/4508410905296976',
    environment: process.env.NODE_ENV,
    
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true, // Privacy protection
        blockAllMedia: true, // Don't record videos/audio  
      }),
    ],
    
    // 💰 COST OPTIMIZATION
    tracesSampleRate: 0.1, // Only track 10% of performance (was 100%)
    replaysSessionSampleRate: 0.05, // Only record 5% of sessions
    replaysOnErrorSampleRate: 1.0, // Always record when errors occur
    
    // 🎯 ERROR FILTERING - Saves money by filtering noise
    beforeSend(event) {
      // Filter out localhost/development errors
      if (event.request?.url?.includes('localhost') || 
          event.request?.url?.includes('127.0.0.1') ||
          event.request?.url?.includes('netlify.app') ||
          !event.request?.url?.includes('staffluent.co')) {
        return null
      }
      
      // Filter out common non-critical errors
      const errorMessage = event.exception?.values?.[0]?.value || ''
      
      if (errorMessage.includes('Network Error') ||
          errorMessage.includes('Loading chunk') ||
          errorMessage.includes('Script error') ||
          errorMessage.includes('ResizeObserver loop limit exceeded') ||
          errorMessage.includes('Non-Error promise rejection captured')) {
        return null // Don't waste quota on these
      }
      
      return event
    },

    // Set proper tracking targets
    tracePropagationTargets: [
      "staffluent.co",
      /^https:\/\/staffluent\.co\/api/
    ],
    
    // Release tracking (Netlify sets this automatically)
    release: process.env.REACT_APP_SENTRY_RELEASE || process.env.NETLIFY_BUILD_ID,
  })
}

// Initialize Sentry
initSentry()

// PWA SERVICE WORKER REGISTRATION
async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      // Wait for OneSignal to register first to avoid conflicts
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Register your Workbox service worker (compiled from src/sw.ts)
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'
      })


      // Handle service worker updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New content available, show update notification
              showUpdateNotification(newWorker)
            }
          })
        }
      })

      // Listen for Workbox messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        // @ts-ignore
        const { type, payload } = event.data
        
        switch (type) {
          case 'CACHE_UPDATED':
            break
          case 'OFFLINE_READY':
            showOfflineReadyNotification()
            break
          case 'SW_ACTIVATED':
            break
          case 'BACKGROUND_SYNC_SUCCESS':
            break
        }
      })

      return registration
    } catch (error) {
      
      // Only report SW errors to Sentry in production
      if (process.env.NODE_ENV === 'production') {
        Sentry.captureException(error, {
          tags: {
            type: 'service_worker_error'
          }
        })
      }
    }
  }
}

// PWA UPDATE NOTIFICATION (Works with Workbox auto-update)
function showUpdateNotification(newWorker?: ServiceWorker) {
  // Create update notification
  const updateBanner = document.createElement('div')
  updateBanner.id = 'update-banner'
  updateBanner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #0a0a0a;
    color: white;
    padding: 12px;
    text-align: center;
    z-index: 9999;
    font-family: system-ui, -apple-system, sans-serif;
  `
  updateBanner.innerHTML = `
    <span>📱 New version available!</span>
    <button id="update-btn" style="margin-left: 12px; padding: 4px 12px; background: white; color: #0a0a0a; border: none; border-radius: 4px; cursor: pointer;">
      Update Now
    </button>
    <button id="dismiss-btn" style="margin-left: 8px; padding: 4px 8px; background: transparent; color: white; border: 1px solid white; border-radius: 4px; cursor: pointer;">
      Later
    </button>
  `

  document.body.appendChild(updateBanner)

  // Handle update button click
  document.getElementById('update-btn')?.addEventListener('click', () => {
    // @ts-ignore
    if (newWorker) {
      newWorker.postMessage({ type: 'SKIP_WAITING' })
    }
    window.location.reload()
  })

  // Handle dismiss button click
  document.getElementById('dismiss-btn')?.addEventListener('click', () => {
    updateBanner.remove()
  })
}

// OFFLINE READY NOTIFICATION
function showOfflineReadyNotification() {
  
  // We can show a toast notification here
  // toast({ title: "Offline Ready", description: "Staffluent is ready for offline use" })
}

// PWA INITIALIZATION
async function initializePWA() {
  try {

    // Initialize IndexedDB
    await indexedDBService.init()

    // Initialize cache service
    if (cacheService.isSupported()) {
      // do nothing for now
    }

    // Initialize location service
    if (locationService.isGeolocationAvailable()) {
      // do nothing for now
    }

    // Setup network status monitoring
    setupNetworkMonitoring()

    // Register service worker
    await registerServiceWorker()

    // Preload critical data when online
    if (navigator.onLine) {
      await preloadCriticalData()
      // Trigger any pending Workbox sync
      triggerWorkboxSync()
    }

  } catch (error) {
    
    // Only report PWA errors to Sentry in production
    if (process.env.NODE_ENV === 'production') {
      Sentry.captureException(error, {
        tags: {
          type: 'pwa_initialization_error'
        }
      })
    }
  }
}

// NETWORK STATUS MONITORING
function setupNetworkMonitoring() {
  const updateOnlineStatus = () => {
    const isOnline = navigator.onLine
    
    // Dispatch custom event for components to listen to
    window.dispatchEvent(new CustomEvent('networkStatusChange', {
      detail: { isOnline }
    }))

    // Update UI indicator
    updateNetworkIndicator(isOnline)
  }

  window.addEventListener('online', updateOnlineStatus)
  window.addEventListener('offline', updateOnlineStatus)
  
  // Trigger Workbox sync when coming back online
  window.addEventListener('online', () => {
    setTimeout(() => {
      triggerWorkboxSync()
    }, 2000) // Small delay to ensure connection is stable
  })
  
  // Initial check
  updateOnlineStatus()
}

// NETWORK INDICATOR
function updateNetworkIndicator(isOnline: boolean) {
  let indicator = document.getElementById('network-indicator')
  
  if (!indicator) {
    indicator = document.createElement('div')
    indicator.id = 'network-indicator'
    indicator.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 8px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      z-index: 1000;
      transition: all 0.3s ease;
    `
    document.body.appendChild(indicator)
  }

  if (isOnline) {
    // no need for indicator
    // indicator.style.background = '#10b981'
    // indicator.style.color = 'white'
    // indicator.textContent = '🟢 Online'
    
    // // Hide after 2 seconds when online
    // setTimeout(() => {
    //   if (indicator) indicator.style.opacity = '0'
    // }, 2000)
  } else {
    indicator.style.background = '#ef4444'
    indicator.style.color = 'white'
    indicator.style.opacity = '1'
    indicator.textContent = '🔴 Offline'
  }
}

// MANUAL SYNC WITH WORKBOX QUEUES
function triggerWorkboxSync() {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    // Trigger your Workbox background sync queues
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_DATA',
      syncType: 'time-tracking'
    })
    
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_DATA', 
      syncType: 'tasks'
    })
    
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_DATA',
      syncType: 'location'
    })
    
    navigator.serviceWorker.controller.postMessage({
      type: 'SYNC_DATA',
      syncType: 'offline'
    })
  }
}

// PRELOAD CRITICAL DATA - Fixed: Added missing function declaration
async function preloadCriticalData() {
  try {
    
    // Get user ID from localStorage or session
    const userId = localStorage.getItem('userId')
    if (!userId) return

    // Preload user profile data
    const criticalDataEndpoints = [
      { 
        type: 'user-data', 
        url: `/api/users/${userId}/profile`, 
        fetchFn: () => fetch(`/api/users/${userId}/profile`).then(r => r.json()) 
      },
      { 
        type: 'projects', 
        url: '/api/projects/active', 
        fetchFn: () => fetch('/api/projects/active').then(r => r.json()) 
      },
      { 
        type: 'tasks', 
        url: '/api/tasks/my-tasks', 
        fetchFn: () => fetch('/api/tasks/my-tasks').then(r => r.json()) 
      }
    ]

    await cacheService.preloadCriticalData(criticalDataEndpoints)
  } catch (error) {
  }
}

// CLEANUP ON UNLOAD
window.addEventListener('beforeunload', () => {
  // Cleanup location tracking
  locationService.cleanup()
  
  // Clean up any pending operations
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CLEANUP',
      timestamp: Date.now()
    })
  }
})

// INITIALIZE PWA ON LOAD
window.addEventListener('load', () => {
  initializePWA()
})

// HANDLE APP FOCUS/BLUR FOR BATTERY OPTIMIZATION
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // App is hidden, reduce location update frequency
  } else {
    // App is visible, resume normal operation
  }
})

// @TODO: remove these logs in the future

// STORAGE QUOTA MANAGEMENT
if ('storage' in navigator && 'estimate' in navigator.storage) {
  navigator.storage.estimate().then(estimate => {
    const used = estimate.usage || 0
    const quota = estimate.quota || 0
    const percentUsed = quota > 0 ? (used / quota) * 100 : 0
    
    // Warn if storage is getting full
    if (percentUsed > 80) {
      // do nothing, later we can implement a notification service or something
    }
  })
}

// RENDER APP
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
        <SessionProvider>
          {process.env.NODE_ENV === 'production' ? (
            <Sentry.ErrorBoundary 
              // @ts-ignore
              fallback={({ error, resetError }) => (
                <div style={{ 
                  padding: '40px', 
                  textAlign: 'center', 
                  fontFamily: 'system-ui',
                  maxWidth: '500px',
                  margin: '50px auto'
                }}>
                  <h2 style={{ color: '#cb0001', marginBottom: '20px' }}>
                    Oops! Something went wrong
                  </h2>
                  <p style={{ color: '#666', marginBottom: '30px' }}>
                    We've been notified and are working on a fix.
                  </p>
                  <button 
                    onClick={resetError}
                    style={{
                      backgroundColor: '#090a0b',
                      color: 'white',
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Try Again
                  </button>
                </div>
              )}
            >
              <RouterProvider router={router} />
              <Toaster />
            </Sentry.ErrorBoundary>
          ) : (
            <>
              <RouterProvider router={router} />
              <Toaster />
            </>
          )}
        </SessionProvider>
      </ThemeProvider>
    </Provider>
  </React.StrictMode>
)